import React, {useState, useEffect, useCallback} from 'react';
import {Link} from 'react-router-dom';
import {useAiAvailable} from '../contexts/AiAvailability';
import {
  fetchHebrewVerbs,
  fetchHebrewVerbDetail,
  fetchAvailableBinyans,
  fetchAvailableRoots,
  importHebrewVerbFromPealim,
  updateHebrewVerbMnemonic,
  updateHebrewVerbSentences,
  fetchVerbDrillProgress,
} from '../helpers/hebrewVerbsApi';
import {
  generateHebrewVerbMnemonic,
  generateHebrewVerbBinyanHint,
  generateHebrewVerbSentences,
  textToSpeech,
} from '../helpers/externalApi';
import {VerbConjugationTable} from '../components/VerbConjugationTable';
import {RootColoredText} from '../components/RootColoredText';
import {
  parseRootLetters,
  getRootLetterInfo,
  buildRootMemoryMnemonic,
} from '../helpers/rootLetterMnemonics';
import {summarizeVerbProgress, MASTERY_BUCKETS} from '../helpers/verbProgress';

const SearchIcon = () => (
  <svg className="verbs-search-bar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const BackChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const StarIcon = ({filled}) => (
  <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export const HebrewVerbsLibrary = () => {
  const {aiAvailable} = useAiAvailable();
  const [verbs, setVerbs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [rootInput, setRootInput] = useState('');
  // Debounced values actually drive the API queries so typing does not
  // fire a request per keystroke.
  const [search, setSearch] = useState('');
  const [selectedRoot, setSelectedRoot] = useState('');
  const [selectedBinyan, setSelectedBinyan] = useState('');
  const [rootLetter, setRootLetter] = useState('');
  const [binyans, setBinyans] = useState([]);
  const [roots, setRoots] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [selectedVerb, setSelectedVerb] = useState(null);
  const [formsCache, setFormsCache] = useState({});
  const [isFavorited, setIsFavorited] = useState(false);
  const [generatingMnemonic, setGeneratingMnemonic] = useState(false);
  const [verbSentences, setVerbSentences] = useState([]);
  const [generatingSentences, setGeneratingSentences] = useState(false);
  const [pronounceLoading, setPronounceLoading] = useState(false);
  const [progressMap, setProgressMap] = useState({});

  const LIMIT = 20;

  // Load per-form drill progress once so the library can surface mastery for
  // the selected verb and per cell in the conjugation table.
  useEffect(() => {
    let cancelled = false;
    fetchVerbDrillProgress()
      .then((map) => {
        if (!cancelled) setProgressMap(map || {});
      })
      .catch(() => {
        if (!cancelled) setProgressMap({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSelectedRoot(rootInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [rootInput]);

  useEffect(() => {
    fetchAvailableBinyans()
      .then((data) => setBinyans(data || []))
      .catch(() => setBinyans([]));
  }, []);

  useEffect(() => {
    fetchAvailableRoots({search: selectedRoot})
      .then((data) => setRoots(data || []))
      .catch(() => setRoots([]));
  }, [selectedRoot]);

  const loadVerbs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchHebrewVerbs({
        search,
        root: selectedRoot,
        binyan: selectedBinyan,
        rootLetter,
        page,
        limit: LIMIT,
      });
      setVerbs(result.verbs || []);
      setTotalCount(result.total || 0);
    } catch (err) {
      console.error('Failed to load verbs:', err);
      setVerbs([]);
    } finally {
      setLoading(false);
    }
  }, [search, selectedRoot, selectedBinyan, rootLetter, page]);

  useEffect(() => {
    loadVerbs();
  }, [loadVerbs]);

  const handleGenerateMnemonic = useCallback(async (verb) => {
    if (!aiAvailable) return;
    setGeneratingMnemonic(true);
    try {
      const [mnemonic, binyanHint] = await Promise.all([
        generateHebrewVerbMnemonic({
          infinitiveHe: verb.infinitive_he,
          transliteration: verb.transliteration,
          meaning: verb.meaning || verb.infinitive_en,
          binyan: verb.binyan,
        }),
        generateHebrewVerbBinyanHint({
          infinitiveHe: verb.infinitive_he,
          transliteration: verb.transliteration,
          meaning: verb.meaning || verb.infinitive_en,
          binyan: verb.binyan,
        }),
      ]);

      if (mnemonic || binyanHint) {
        await updateHebrewVerbMnemonic({
          verbId: verb.id,
          mnemonic: mnemonic || '',
          binyanHint: binyanHint || '',
        });
        loadVerbs();
        setSelectedVerb(prev =>
          prev && prev.id === verb.id
            ? {...prev, mnemonic: mnemonic || '', binyanHint: binyanHint || ''}
            : prev,
        );
      }
    } catch (err) {
      console.warn('Mnemonic generation failed:', err);
    } finally {
      setGeneratingMnemonic(false);
    }
  }, [aiAvailable, loadVerbs]);

  const handleGenerateSentences = useCallback(async (verb) => {
    if (!aiAvailable) return;
    setGeneratingSentences(true);
    try {
      const sentences = await generateHebrewVerbSentences({
        infinitiveHe: verb.infinitive_he,
        transliteration: verb.transliteration,
        meaning: verb.meaning || verb.infinitive_en,
        binyan: verb.binyan,
      });
      if (sentences.length > 0) {
        const json = JSON.stringify(sentences);
        await updateHebrewVerbSentences({verbId: verb.id, sentences: json});
        setVerbSentences(sentences);
        setSelectedVerb(prev =>
          prev && prev.id === verb.id
            ? {...prev, sample_sentences: json}
            : prev,
        );
      }
    } catch (err) {
      console.warn('Sentence generation failed:', err);
    } finally {
      setGeneratingSentences(false);
    }
  }, [aiAvailable]);

  const handlePronounce = useCallback(async (text) => {
    if (!aiAvailable || !text) return;
    setPronounceLoading(true);
    try {
      const blob = await textToSpeech({text});
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.play();
    } catch (err) {
      console.warn('TTS failed:', err.message);
    } finally {
      setPronounceLoading(false);
    }
  }, [aiAvailable]);

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    setImportMessage('');
    try {
      const result = await importHebrewVerbFromPealim(importUrl);
      loadVerbs();

      const importedVerb = result.verb;
      if (importedVerb && aiAvailable) {
        await handleGenerateMnemonic(importedVerb);
      }

      setImportMessage('Verb imported successfully.');
      setImportUrl('');
    } catch (err) {
      setImportMessage(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleSelectVerb = async (verb) => {
    setSelectedVerb(verb);
    setIsFavorited(false);
    try {
      const saved = verb.sample_sentences
        ? JSON.parse(verb.sample_sentences)
        : [];
      setVerbSentences(Array.isArray(saved) ? saved : []);
    } catch (_) {
      setVerbSentences([]);
    }
    if (!formsCache[verb.id]) {
      try {
        const detail = await fetchHebrewVerbDetail(verb.id);
        setFormsCache((prev) => ({...prev, [verb.id]: detail.forms || []}));
      } catch (err) {
        setFormsCache((prev) => ({...prev, [verb.id]: []}));
      }
    }
  };

  const handleBack = () => {
    setSelectedVerb(null);
  };

  const resetTextFilters = () => {
    setSearchInput('');
    setSearch('');
    setRootInput('');
    setSelectedRoot('');
    setRootLetter('');
  };

  const browseByRoot = (root) => {
    resetTextFilters();
    setSelectedBinyan('');
    setRootInput(root);
    setSelectedRoot(root);
    setSelectedVerb(null);
    setPage(1);
  };

  const browseByRootLetter = (letter) => {
    resetTextFilters();
    setSelectedBinyan('');
    setRootLetter(letter);
    setSelectedVerb(null);
    setPage(1);
  };

  const browseByBinyan = (binyan) => {
    resetTextFilters();
    setSelectedBinyan(binyan);
    setSelectedVerb(null);
    setPage(1);
  };

  const clearRootLetter = () => {
    setRootLetter('');
    setPage(1);
  };

  const totalPages = Math.ceil(totalCount / LIMIT);
  const hasMore = page < totalPages;
  const hasPrev = page > 1;

  if (selectedVerb) {
    const rootLetters = parseRootLetters(selectedVerb.root);
    const verbForms = formsCache[selectedVerb.id] || [];
    const verbProgress = verbForms.length > 0 ? summarizeVerbProgress(verbForms, progressMap) : null;
    const masteredPct = verbProgress && verbProgress.total > 0
      ? Math.round((100 * verbProgress.mastered) / verbProgress.total)
      : 0;
    return (
      <div className="verb-detail-page">
        <div className="verb-detail-nav">
          <button type="button" className="verb-nav-btn" onClick={handleBack} aria-label="Go back">
            <BackChevron />
          </button>
          <button
            type="button"
            className="verb-nav-btn"
            onClick={() => setIsFavorited(!isFavorited)}
            aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <StarIcon filled={isFavorited} />
          </button>
        </div>

        <div className="verb-header-card">
          <div className="verb-header-top-row">
            <div
              role="button"
              tabIndex={0}
              className="verb-header-root verb-root-link font-hebrew"
              title="Show verbs with this root"
              onClick={() => browseByRoot(selectedVerb.root)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  browseByRoot(selectedVerb.root);
                }
              }}
            >
              {rootLetters.length > 0 ? (
                rootLetters.map((letter, i) => {
                  const info = getRootLetterInfo(letter);
                  return (
                    <React.Fragment key={i}>
                      {i > 0 && <span className="verb-root-sep" aria-hidden="true"> - </span>}
                      <button
                        type="button"
                        className="verb-root-letter verb-root-letter--colored"
                        style={{color: info.color}}
                        title={`${info.icon} ${info.name} (${info.mnemonic}) — show verbs with this root letter`}
                        onClick={(e) => {
                          e.stopPropagation();
                          browseByRootLetter(letter);
                        }}
                      >
                        {letter}
                      </button>
                    </React.Fragment>
                  );
                })
              ) : (
                selectedVerb.root
              )}
            </div>
            <button
              type="button"
              className="verb-header-binyan-pill"
              title="Show verbs in this binyan"
              onClick={() => browseByBinyan(selectedVerb.binyan)}
            >
              {selectedVerb.binyan}
            </button>
            {selectedVerb.binyanHint && (
              <span className="verb-header-binyan-hint">{selectedVerb.binyanHint}</span>
            )}
          </div>
          <div className="verb-header-infinitive-row">
            <div className="verb-header-infinitive font-hebrew">
              <RootColoredText text={selectedVerb.infinitive_he} rootLetters={rootLetters} />
            </div>
            {aiAvailable && (
              <button
                type="button"
                className="verb-pronounce-btn"
                onClick={() => handlePronounce(selectedVerb.infinitive_he)}
                disabled={pronounceLoading}
                aria-label="Pronounce infinitive"
              >
                {pronounceLoading ? '⏳' : '🔊'}
              </button>
            )}
          </div>
          {selectedVerb.transliteration && (
            <div className="verb-header-translit">{selectedVerb.transliteration}</div>
          )}
          <div className="verb-header-meaning">
            {selectedVerb.meaning || selectedVerb.infinitive_en}
          </div>
          {generatingMnemonic && (
            <div className="verb-header-mnemonic-loading">
              Generating mnemonic...
            </div>
          )}
          {!generatingMnemonic && selectedVerb.mnemonic && (
            <div className="verb-header-mnemonic">{selectedVerb.mnemonic}</div>
          )}
          {aiAvailable && !generatingMnemonic && (
            <button
              type="button"
              className="verb-header-mnemonic-btn"
              onClick={() => handleGenerateMnemonic(selectedVerb)}
            >
              {selectedVerb.mnemonic ? 'Regenerate mnemonic' : 'Generate mnemonic'}
            </button>
          )}

          {rootLetters.length > 0 && (
            <div className="root-memory-card">
              <div className="root-memory-chips" dir="rtl">
                {rootLetters.map((letter, i) => {
                  const info = getRootLetterInfo(letter);
                  return (
                    <span
                      key={i}
                      className="root-memory-chip"
                      style={{'--root-color': info.color}}
                    >
                      <span className="root-memory-icon" aria-hidden="true">{info.icon}</span>
                      <span className="root-memory-letter font-hebrew">{letter}</span>
                      <span className="root-memory-name">{info.name}</span>
                    </span>
                  );
                })}
              </div>
              <p className="root-memory-story">
                {buildRootMemoryMnemonic(rootLetters, selectedVerb.meaning || selectedVerb.infinitive_en)}
              </p>
            </div>
          )}
        </div>

        {verbProgress && (
          <div className="verb-progress-card">
            <div className="verb-progress-head">
              <h3 className="verb-progress-title">Verb mastery</h3>
              <span className="verb-progress-overall" style={{color: verbProgress.overall.color}}>
                {verbProgress.overall.label}
              </span>
            </div>
            <div className="verb-progress-bar" aria-label={`${masteredPct}% of forms mastered`}>
              <div className="verb-progress-fill" style={{width: `${masteredPct}%`}} />
            </div>
            <div className="verb-progress-stats">
              <div className="verb-stat">
                <span className="verb-stat-value">{verbProgress.mastered}/{verbProgress.total}</span>
                <span className="verb-stat-label">Mastered</span>
              </div>
              <div className="verb-stat">
                <span className="verb-stat-value">{verbProgress.learning}</span>
                <span className="verb-stat-label">Learning</span>
              </div>
              <div className="verb-stat">
                <span className="verb-stat-value">{verbProgress.newCount}</span>
                <span className="verb-stat-label">New</span>
              </div>
              <div className="verb-stat">
                <span className="verb-stat-value">
                  {verbProgress.accuracy == null ? '—' : `${verbProgress.accuracy}%`}
                </span>
                <span className="verb-stat-label">Accuracy</span>
              </div>
            </div>
            <div className="mastery-legend">
              {MASTERY_BUCKETS.map((b) => (
                <span key={b.key} className="mastery-legend-item">
                  <span className={`mastery-dot mastery-${b.key}`} />
                  {b.label}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="verb-conjugation-sections">
          {!formsCache[selectedVerb.id] ? (
            <div className="conjugation-no-data">Loading conjugations...</div>
          ) : formsCache[selectedVerb.id].length === 0 ? (
            <div className="conjugation-no-data">No conjugations available for this verb.</div>
          ) : (
            <VerbConjugationTable
              forms={formsCache[selectedVerb.id]}
              root={selectedVerb.root}
              progressMap={progressMap}
            />
          )}
        </div>

        <div className="verb-sentences-section">
          <div className="verb-sentences-header">
            <h3 className="verb-sentences-title">Sample Sentences</h3>
            {aiAvailable && (
              <button
                type="button"
                className="verb-header-mnemonic-btn"
                onClick={() => handleGenerateSentences(selectedVerb)}
                disabled={generatingSentences}
              >
                {generatingSentences
                  ? 'Generating...'
                  : verbSentences.length > 0
                    ? 'Regenerate sentences'
                    : 'Generate sentences'}
              </button>
            )}
          </div>
          {generatingSentences && (
            <div className="verb-header-mnemonic-loading">
              Generating sample sentences...
            </div>
          )}
          {!generatingSentences && verbSentences.length > 0 && (
            <div className="verb-sentences-list">
              {verbSentences.map((s, i) => (
                <div key={i} className="verb-sentence-card">
                  <div className="verb-sentence-text font-hebrew">{s.text}</div>
                  {s.transliteration && (
                    <div className="verb-sentence-translit">{s.transliteration}</div>
                  )}
                  <div className="verb-sentence-translation">{s.translation}</div>
                </div>
              ))}
            </div>
          )}
          {!generatingSentences && verbSentences.length === 0 && (
            <p className="verb-sentences-empty">No sample sentences yet.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container verbs-dark-page">
      <div className="verbs-search-bar-wrap">
        <SearchIcon />
        <input
          type="text"
          className="verbs-search-bar-input"
          placeholder="Search verbs..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      <div className="verbs-filters-dark">
        <div className="filter-row">
          <select
            value={selectedBinyan}
            onChange={(e) => {setSelectedBinyan(e.target.value); setPage(1);}}
          >
            <option value="">All Binyans</option>
            {binyans.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <input
            type="text"
            list="hebrew-verb-roots"
            placeholder="Root"
            value={rootInput}
            onChange={(e) => setRootInput(e.target.value)}
          />
          <datalist id="hebrew-verb-roots">
            {roots.map((root) => (
              <option key={root} value={root} />
            ))}
          </datalist>
        </div>
        {rootLetter && (
          <div className="active-filter-row">
            <span className="active-filter-chip">
              Root letter:
              <span className="active-filter-value font-hebrew">{rootLetter}</span>
              <button
                type="button"
                className="active-filter-clear"
                onClick={clearRootLetter}
                aria-label="Clear root letter filter"
              >
                ×
              </button>
            </span>
          </div>
        )}
      </div>

      <div className="verbs-import-card-dark">
        <h3>Import from Pealim</h3>
        <div className="import-form">
          <input
            type="text"
            placeholder="https://www.pealim.com/dict/58-leabed/"
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            disabled={importing}
          />
          <button
            type="button"
            onClick={handleImport}
            disabled={importing || !importUrl.trim()}
          >
            {importing ? 'Importing...' : 'Import'}
          </button>
        </div>
        {importMessage && (
          <div className={`import-message ${importMessage.startsWith('Verb imported') ? 'success' : 'error'}`}>
            {importMessage}
          </div>
        )}
      </div>

      {loading ? (
        <div className="verbs-empty-dark">
          <p>Loading verbs...</p>
        </div>
      ) : verbs.length === 0 ? (
        <div className="verbs-empty-dark">
          <p>No verbs found. Try importing one from Pealim.</p>
        </div>
      ) : (
        <>
          <div className="verbs-result-list">
            {verbs.map((verb) => (
              <button
                type="button"
                key={verb.id}
                className="verbs-result-row"
                onClick={() => handleSelectVerb(verb)}
              >
                <span className="verbs-result-english">
                  {verb.meaning || verb.infinitive_en}
                </span>
                <span className="verbs-result-hebrew font-hebrew">
                  <RootColoredText
                    text={verb.infinitive_he}
                    rootLetters={parseRootLetters(verb.root)}
                  />
                </span>
              </button>
            ))}
          </div>

          <div className="verbs-pagination-dark">
            <button
              type="button"
              disabled={!hasPrev}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={!hasMore}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>

          <div className="verbs-drill-link-dark">
            <Link to="/verb-drill">Practice Verb Forms</Link>
          </div>
        </>
      )}
    </div>
  );
};
